import { store } from "../packages/store.js";

// Set of primitive types used for checking
const primitiveTypes = new Set(["int", "string", "expression", "float", "boolean", "color"]);

class Expressions {
  constructor() {
    this.allPossibleSpecialChars = [
      this.getSnapshotSpecialChar(),
      this.getEventSpecialChar(),
      this.getLinkedPropertySpecialChar(),
    ];
  }

  // Special character getters - keeping these as is for consistency
  getLinkedPropertySpecialChar() {
    return "!";
  }

  getSnapshotSpecialChar() {
    return "$";
  }

  // Backward-compatible alias
  getComputedPropertySpecialChar() {
    return this.getSnapshotSpecialChar();
  }

  getEventSpecialChar() {
    return "@";
  }

  // Optimized property type check
  _isSpecialProperty(string, character) {
    // Early return for non-strings and empty values
    if (!string || typeof string !== "string") {
      return false;
    }
    
    // Since the special character is always followed by '{', 
    // we can use indexOf which is typically faster than includes
    return string.indexOf(character + "{") !== -1;
  }

  // Store property check - kept as is
  isStoreProperty = (string) => {
    const type = string.split(".")[0];
    return type in store.cache;
  };

  // Simple property type checkers - keeping these as is for API consistency
  isLinked = (string) => this._isSpecialProperty(string, "!");
  isEvent = (string) => this._isSpecialProperty(string, "@");
  isSnapshot = (string) => this._isSpecialProperty(string, "$");
  isDynamic = (string) => this.isLinked(string) || this.isEvent(string) || this.isSnapshot(string);
  isNonComputingValue = (string) =>
    typeof string === "string" && !this.isLinked(string) && !this.isEvent(string) && !this.isSnapshot(string);

  /*
    Checks if this property (already processed during evaluation) is an event on an upper level.
    E.g. `@{event.some.data}` -> `true`
    `${some.uid.@{event.some.data}}` -> `false`.
  */
  isPropertyEvent = (string) => string[0] === "@";

  isNonReactive = (string) => this.isEvent(string) || this.isSnapshot(string);

  // Backward-compatible aliases
  isComputed = this.isSnapshot;
  isDynamicNotLinked = this.isNonReactive;
  // isUid method removed - validation now handled directly in engine.js
  // isUid = (string) => /[\dA-z]/.test(string);
  isExpression = (string) => [...string].some((char) => this.isOperator(char));
  isOperator = (char) =>
    char === "/" || char === "*" || char === "+" || char === "-" || char === "%";

  // Refactored string formatting methods
  removeBrackets = (string) => {
    // Removes first two symbols (!/@/${) and the last one that is }
    return string.slice(2, -1);
  };

  removeWhitespace = (string) => string.replaceAll(/\s/g, "");

  /**
   * Extracts property expressions from a string
   * Refactored for clarity while maintaining identical results
   */
  retrieveProperties = (string, specialChars = this.allPossibleSpecialChars) => {
    const specialCharsSet = new Set(specialChars);
    const results = [];

    let currentDepth = 0;
    let expressionStart = -1;
    let isInsideExpression = false;

    // Scan the string character by character
    for (let index = 0; index < string.length; index++) {
      const char = string[index];
      const nextChar = string[index + 1];
      const previousChar = string[index - 1];

      // Check if this is the start of a special expression
      if (specialCharsSet.has(char) && nextChar === "{" && previousChar !== "\\") {
        if (!isInsideExpression) {
          expressionStart = index;
          isInsideExpression = true;
        }
        currentDepth++;
      }

      // Check for closing brackets
      if (char === "}" && isInsideExpression) {
        currentDepth--;

        // If we've closed all open brackets, we've found a complete expression
        if (currentDepth === 0) {
          results.push(string.slice(expressionStart, index + 1));
          isInsideExpression = false;
        }
      }
    }

    // Filter to only include expressions that start with the specified special chars
    return results.filter((expr) => specialChars.some((char) => expr.startsWith(char)));
  };

  /**
   * Refactored to use RegExp for cleaner extraction of expression parts
   * Maintains identical output
   */
  splitExpression = (string_) => {
    const equationArray = [];

    // Extract parts enclosed in curly braces
    const braceMatches = string_.match(/{([^}]+)}/g);
    if (braceMatches) {
      for (const match of braceMatches) {
        // Remove the braces and add to results
        equationArray.push(match.slice(1, -1));
      }
    }

    // Handle operators and numbers
    let remaining = string_;

    // Replace braced content with placeholders to simplify operator processing
    if (braceMatches) {
      for (const match of braceMatches) {
        remaining = remaining.replace(match, " # ");
      }
    }

    // Process operators and numbers
    let buffer = "";

    for (const char of remaining) {
      // Skip placeholders (they're handled separately)
      if (char === "#") continue;

      if (this.isOperator(char)) {
        // If we have a buffer, add it to results
        if (buffer.trim() && buffer !== "{" && buffer !== "}") {
          equationArray.push(buffer.trim());
        }

        // Add the operator
        equationArray.push(char);

        // Reset buffer
        buffer = "";
      } else if (char !== "{" && char !== "}") {
        buffer += char;
      }
    }

    // Add any remaining buffer
    if (buffer.trim() && buffer !== "{" && buffer !== "}") {
      equationArray.push(buffer.trim());
    }

    // Filter out empty strings
    return equationArray.filter((item) => item.trim());
  };

  /**
   * Using RegExp for more maintainable code
   * Maintains identical output
   */
  extractVariablesFromExpression = (string_, specialChar) => {
    const openingDelimiter = specialChar ? `\\${specialChar}{` : `{`;
    const closingDelimiter = `}`;

    const pattern = new RegExp(`(?<=${openingDelimiter})(.*?)(?=${closingDelimiter})`, "g");
    const variables = string_.match(pattern);

    return variables || [];
  };

  /**
   * Extracts property UIDs from expressions
   * Refactored to use RegExp for clarity
   */
  getPropertyUids = (string_) => {
    const pattern = /{([^}]+)}/g;
    const matches = string_.match(pattern);

    if (!matches) return [];

    return matches.map((match) => match.slice(1, -1));
  };

  /**
   * Escapes expressions by handling backslashes
   * Refactored for clarity while maintaining identical behavior
   */
  escapeExpression = (expression) => {
    // Replacing consecutive pairs of backslashes with a single backslash
    return expression.replaceAll(/\\{2}/g, "\\");
  };
}

// Add isPrimitiveType to the Expressions class as an instance method
// Moved from libs/helpers/value-type.js to reduce dependencies
Expressions.prototype.isPrimitiveType = (valueType) => primitiveTypes.has(valueType);

const expressions = new Expressions();
export { Expressions, expressions };
